import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

function StarRating({ value, onChange, readonly = false }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(i)}
          className={`transition-colors ${readonly ? "cursor-default" : "cursor-pointer hover:scale-110"}`}
        >
          <Star className={`w-7 h-7 ${i <= value ? "fill-yellow-400 text-yellow-400" : "text-slate-200"}`} />
        </button>
      ))}
    </div>
  );
}

export default function PlatformReviewDialog({ open, onOpenChange, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(null);
  const [canEdit, setCanEdit] = useState(true);
  const [canEditAt, setCanEditAt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/platform-reviews/my-review")
      .then(r => {
        const rev = r.data.review;
        setExisting(rev);
        setCanEdit(r.data.can_edit ?? true);
        setCanEditAt(r.data.can_edit_at);
        if (rev) {
          setRating(rev.rating);
          setComment(rev.comment || "");
        } else {
          setRating(0);
          setComment("");
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error("Veuillez sélectionner une note");
      return;
    }
    if (existing && !canEdit) {
      toast.error(`Vous pourrez modifier votre avis à partir du ${new Date(canEditAt).toLocaleDateString("fr-FR")}`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/platform-reviews", { rating, comment: comment.trim() });
      toast.success(existing ? "Avis mis à jour !" : "Merci pour votre avis !");
      onOpenChange(false);
      onSubmitted?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {existing ? "Votre avis sur MyCoaching" : "Évaluez MyCoaching"}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : (
          <div className="space-y-5 mt-2">
            <p className="text-sm text-slate-500">
              Partagez votre expérience pour aider d'autres coachs à découvrir la plateforme.
            </p>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Note</p>
              <StarRating value={rating} onChange={setRating} readonly={existing && !canEdit} />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Commentaire (optionnel)</p>
              <Textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Qu'avez-vous apprécié ou souhaiteriez-vous améliorer ?"
                rows={3}
                disabled={existing && !canEdit}
                className="resize-none"
              />
            </div>
            {existing && !canEdit && canEditAt && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                Modification possible à partir du {new Date(canEditAt).toLocaleDateString("fr-FR")} (8 semaines entre chaque mise à jour)
              </p>
            )}
            <Button
              onClick={handleSubmit}
              disabled={submitting || (existing && !canEdit)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> {existing ? "Mettre à jour" : "Publier mon avis"}</>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}